import { type EpisodeOfCareInfo, type UserSessionData } from 'src/types/webContracts'
import { type NamedPerson } from 'src/types/interfaces';
import { computed, ref, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { MEDSTRAT_HOLDING } from '@/constants/clients';
import { HackedProcedures } from '@/constants/hacks';
import { i18n } from '@/plugins/i18n';
import { EpisodeOfCareStatus } from '@/constants/enums';
import { getCustomerSupportInfo } from '@/api/endpoints/loginWeb'
import type { PatientSupportResponse, AppDownloadResult } from '@/types/storeTypes'
export const useSessionStore = defineStore('session', () => {

  const user: Ref<UserSessionData | null> = ref(null);
  const isAuthenticated = ref(false);
  const isHeaderVisible = ref(false);
  const isFooterVisible = ref(true);
  const hasCookie = ref(false);
  const completedLogin = ref(false);
  const hasPasscode = ref(false);
  const utcOffset = ref(Math.floor(new Date().getTimezoneOffset() / -60));
  const procedures: Ref<EpisodeOfCareInfo[] | null> = ref(null);
  const supportInfo: Ref<PatientSupportResponse | null> = ref(null);
  const appDownloadInfo: Ref<AppDownloadResult | null> = ref(null);
  const cobrandedImages: Ref<Record<string, string> | null> = ref(null)
  const cobrandedCssRules: Ref<string | null> = ref(null)
  const userId = computed(() => user.value?.userId)
  const isMedstratHoldingTankUser = computed(() => user.value?.clientName === MEDSTRAT_HOLDING)
  const medstratLinkPatientId = computed(() => user.value?.medstratPatientId)
  const magicLinkEocId = ref<number | null>(null);
  const isMagicLinkUser = computed(() => user.value?.policies.includes('FullTrustOrMagicLinkTrustPatient') &&
    !user.value?.policies.includes('FullTrustPatient'));
  const requiresPasswordChange = computed(() => user.value?.requiresPasswordChange === true);
  const selectedProcedure = computed(() => procedures.value?.find(p => p.eocId === user.value?.selectedEocId) || null);

  const setUserFields = (userData: UserSessionData | null) => {
    user.value = userData ? { ...userData } : null;
  }

  const setCobranding = (images: Record<string, string> | null, colorRules: string | null) => {
    cobrandedImages.value = images ? { ...images } : null
    cobrandedCssRules.value = colorRules
  }

  const loadCustomerSupportInfo = async (countryCode: string, locale: string) => {
    const response = await getCustomerSupportInfo(countryCode, locale)
    if (response?.data?.result) {
      appDownloadInfo.value = response.data.result;
      const supportData = response.data.result.preambleResult?.support ?? {};
      const supportConfig = response.data.result.preambleResult?.countrySupportConfigurations?.find(
        config => config.countryCode === countryCode
      );

      if (supportConfig) {
        supportInfo.value = {
          ...supportData,
          countrySupportEmail: supportConfig.email,
          countrySupportPhone: supportConfig.phone,
        };
      } else {
        supportInfo.value = { ...supportData };
      }
    }
    return supportInfo.value
  }

  const setCompletedLogin = (completedLoginValue: boolean) => {
    completedLogin.value = completedLoginValue;
  }

  const setIsAuthenticated = (flag: boolean) => {
    isAuthenticated.value = flag;
  }

  const setProcedures = (proceduresValue: EpisodeOfCareInfo[] | null) => {
    procedures.value = proceduresValue;
  }

  const setSelectedEocId = (selectedEocId: number) => {
    if (user.value) {
      user.value.selectedEocId = selectedEocId;
    }
  }

  const setMagicLinkEocId = (eocId: number | null) => {
    magicLinkEocId.value = eocId;
    }

  const setHasPasscode = (isPasscode: boolean) => {
    hasPasscode.value = isPasscode;
  }

  const load = (userData: UserSessionData) => {
    setUserFields(userData);
    setIsAuthenticated(true);
  }

  const loadProcedures = (proceduresValue: EpisodeOfCareInfo[]) => {

    if (medstratLinkPatientId.value) {
      proceduresValue = [...proceduresValue, {
        eocId: HackedProcedures.ImageViewer,
        surgeryDate: null,
        procedureNameLocalized: i18n.global.t('acc_image_viewer').toString(),
        careTeamName: '',
        protocolNameLocalized: '',
        protocolLevelLocalized: '',
        bodyPartName: 'Image_Viewer',
        bodyPartIcon: 'fa-regular fa-x-ray',
        lateralityNameLocalized: '',
        lateralityBodyPartLocalized: i18n.global.t('acc_image_viewer').toString(),
        lateralityBodyPartAbbrLocalized: i18n.global.t('acc_image_viewer').toString(),
        lateralityAbbreviationLocalized: '',
        status: EpisodeOfCareStatus.Active,
        alerts: {
          videoVisit: 0,
          assessment: 0,
          messages: 0,
          tasks: 0
        },
        totalAlerts: 0,
        hasEpisodeException: false,
        isROMPatient: false,
        isSurgicalProcedure: false
      }]

      const shellProcedure = proceduresValue.find(p => p.eocId > 0 && !p.surgeryDate)

      //Give it a kvp for rare circumstances were displaying the shell eoc is required to give access to messaging
      if (shellProcedure) {
        shellProcedure.procedureNameLocalized = i18n.global.t('pwe_home').toString();
        shellProcedure.bodyPartName = 'Shell_Procedure';
        shellProcedure.bodyPartIcon = 'fa-regular fa-house';
        shellProcedure.lateralityBodyPartLocalized = i18n.global.t('pwe_home').toString();
        shellProcedure.lateralityBodyPartAbbrLocalized = i18n.global.t('pwe_home').toString();
      }

      //medstrat-only patients within the holding tank do not have access to features like messaging, so filter out the shell eoc
      if (user.value?.clientName === MEDSTRAT_HOLDING) {
        proceduresValue = proceduresValue.filter(p => !(p.eocId > 0 && !p.surgeryDate));
      }
    }

    setProcedures(proceduresValue);

    return [...(procedures.value || [])]
  }

  const clear = () => {
    setIsAuthenticated(false);
    setUserFields(null);
    setIsHeaderVisible(false);
    setProcedures(null);
  }

  const setIsHeaderVisible = (isVisible: boolean) => {
    isHeaderVisible.value = isVisible;
  }

  const setIsFooterVisible = (isVisible: boolean) => {
    isFooterVisible.value = isVisible;
  }

  function namedPerson(): NamedPerson {
    if (!user.value) return {};
    return {
      firstName: user.value.firstName,
      lastName: user.value.lastName,
    } as NamedPerson
  }

  const CobrandedImage = computed(() => (key: string) => cobrandedImages.value ? cobrandedImages.value[key] : null);
  const isCobrandingEnabled = computed(() => !!(cobrandedImages.value || cobrandedCssRules.value))

  //written oddly to ensure specifically that it's not a negative number, undefined null, and positive integers are "proper"
  const isProperProcedure = computed(() => !(user.value?.selectedEocId < 0))

  //exclude shell eocs and include hacked procedures
  //include shell eoc if it has a kvp
  const navigatableProcedures = (proceduresValue: EpisodeOfCareInfo[]) => {
    let procs = proceduresValue.filter(p => (p.eocId > 0 && (p.surgeryDate || p.lateralityBodyPartAbbrLocalized || p.lateralityNameKey == 'OrientationType_None')) || p.eocId < 0) || []
    //medstrat-only patients within the holding tank do not have access to features like messaging, so filter out the shell eoc
    if (user.value?.clientName === MEDSTRAT_HOLDING) {
      procs = procs.filter(p => !(p.eocId > 0 && !p.surgeryDate));
    }

    return procs
  }
  const activeProcedures = computed(() => {
    return (procedures.value || []).filter(
        (procedure) => procedure.status === EpisodeOfCareStatus.Active && procedure.isSurgicalProcedure
    );
  });

  return {
    user,
    userId,
    isAuthenticated,
    isHeaderVisible,
    isFooterVisible,
    hasCookie,
    hasPasscode,
    magicLinkEocId,
    completedLogin,
    isMagicLinkUser,
    utcOffset,
    procedures,
    supportInfo,
    appDownloadInfo,
    setUserFields,
    setCompletedLogin,
    setIsAuthenticated,
    setProcedures,
    setSelectedEocId,
    setMagicLinkEocId,
    setHasPasscode,
    load,
    loadProcedures,
    clear,
    setIsHeaderVisible,
    setIsFooterVisible,
    namedPerson,
    cobrandedImages,
    cobrandedCssRules,
    setCobranding,
    CobrandedImage,
    isCobrandingEnabled,
    isMedstratHoldingTankUser,
    medstratLinkPatientId,
    isProperProcedure,
    navigatableProcedures,
    activeProcedures,
    requiresPasswordChange,
    selectedProcedure,
    loadCustomerSupportInfo
  }
});
